import { Card, Typography, TextField, Divider, FormGroup, MenuItem, Stack, Grid, Badge, Box } from "@mui/material";
import { useGetMainList, useShowContext } from "ra-core";
import { useEffect } from "react";
import { ChangeEvent, FC, useCallback, useMemo } from "react";
import { RecPatch } from "@/dataProvider/tools";
import { pages, sorts } from "@/utils/paramsWithGetMainList";
import { BasicInfoRow, BasicInfoPiece } from "../../backups/BackupBasicInfoCard";
import { ObfuscationMappingResponseDto, TableObfsMappingCountDto } from "@/gen/api/encrypt";
import { useEditObfsMappingContext } from "./EditObfsMappingContext";



export const ObfsTableSelectionCard: FC = () => {
  const { databaseId, currTable, switchTable } = useEditObfsMappingContext()

  const { record } = useShowContext<ObfuscationMappingResponseDto & RecPatch>()
  const { data: dbTablesMap } = 
    useGetMainList<TableObfsMappingCountDto & RecPatch>(!databaseId ? 'noop' : 'obfs-table', pages(), sorts(), { databaseId })
  const dbTables = useMemo(() => 
    Object.values(dbTablesMap || {})
      .sort((a, b) => a.schema === b.schema 
        ? ((a.name || '') < (b.name || '') ? -1 : 1)
        : ((a.schema || '') < (b.schema || '') ? -1 : 1)), [dbTablesMap])
    
  const handleTableChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const selectedTable = dbTables.find(x => x.tableId === +e.target.value) || null
    switchTable(selectedTable)
  }, [dbTables, switchTable])
  
  useEffect(() => {
    if (!currTable && dbTables.length > 0) {
      switchTable(dbTables[0])
    }
    if (currTable) {
      if (dbTables.length > 0 && dbTables.every(d => d.tableId !== currTable.tableId)) {
        switchTable(dbTables[0])
      }
    }
  }, [currTable, switchTable, dbTables.length])

  return (
    <Card>
      <BasicInfoRow>
        <BasicInfoPiece>
          <Typography
            color="textPrimary"
            variant="h6"
          >
            Database
          </Typography>
          <Typography
            color="textSecondary"
            variant="body1"
          >
            {record?.databaseName}
          </Typography>
        </BasicInfoPiece>
        <BasicInfoPiece>
          <Typography
            color="textPrimary"
            variant="h6"
          >
            Engine
          </Typography>
          <Typography
            color="textSecondary"
            variant="body1"
          >
            {record?.engineVersion + ' ' + (record?.engineEdition || '')}
          </Typography>
        </BasicInfoPiece>
      </BasicInfoRow>
      <Divider />
      <Grid
        container
        sx={{
          px: 3,
          py: 1,
          alignItems: 'center',
        }}
      >
        <Grid item xs={8}>
          <Grid container>
            <Grid item xs={3}>            
              <Typography
                id="table-name"
                color="textPrimary"
                variant="h6"
                sx={{ flex: 1 }}
              >
                Table Name
              </Typography>
            </Grid>            
            <Grid item xs={9} sx={listSx}>            
              <FormGroup>
                <TextField
                  value={currTable?.tableId || dbTables[0]?.tableId || ''}
                  onChange={handleTableChange}
                  select
                  size="small"
                  id='table-select'
                  SelectProps={{
                    inputProps: {
                      labelId: 'table-name',
                    },
                    MenuProps: { PaperProps: { sx: listSx } }
                  }}
                >
                  {dbTables.map(t => (
                    <MenuItem key={t.id} value={t.tableId}>
                      <Box
                        className="gcontainer"
                        sx={{ 
                          width: '100%',
                          '& span.MuiTypography-body2': { 
                            maxWidth: 320,
                            wordBreak: 'keep-all',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            mr: 'auto',
                          }
                        }}
                      >
                        <Typography component="span" variant="caption" color="InactiveCaptionText"
                          className="i1"
                        >
                          {t.schema}.
                        </Typography>
                        <Typography component="span" variant="body2" 
                          className="i2"  
                        >
                          {t.name}
                        </Typography>
                        <Typography component="span" variant="caption" 
                          className="i3"
                          sx={{ color: 'success.light', mr: 2 }}
                        >
                          <Badge 
                            color={"success" as any} 
                            badgeContent={t.id === currTable?.tableId 
                              ? ((t.obfsMappedCount || 0)) 
                              : (t.obfsMappedCount || 0)} 
                            // showZero 
                            max={9999} 
                          />
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </TextField>
              </FormGroup>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={6} sx={{ display: 'flex' }}>
        </Grid>
      </Grid>
    </Card>
  )
}

const listSx = {
  '& .gcontainer': {                          
    display: 'grid',
    gridAutoColumns: 'minmax(50px, auto) 1fr auto',
    columnGap: 1,
    '& .i1': {
      lineHeight: '1.8',
      textAlign: 'right',
      gridColumn: '1 / span 1'
    },
    '& .i2': { gridColumn: '2 / span 1' },
    '& .i3': { gridColumn: '3 / span 1' }
  }
}


const toSignedString = (x:number) => (Math.abs(x) === x ? '+' : '-') + Math.abs(x)

